import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  LegacyCard,
  FormLayout,
  Page,
  TextField,
  Layout,
  Select,
  IndexTable,
  EmptyState,
  IndexFilters,
  useSetIndexFiltersMode,
} from "@shopify/polaris";
import type { IndexTableHeading } from "@shopify/polaris/build/ts/src/components/IndexTable";
import type { SortButtonChoice } from "@shopify/polaris";
import type { NonEmptyArray } from "@shopify/polaris/build/ts/src/types";
import { ImportIcon, ExportIcon } from "@shopify/polaris-icons";
import { useNavigate, useParams } from "react-router-dom";
import HorseVariant from "../common/HorseVariant/HorseVariant";
import { ErrorBanner } from "../common/ErrorBanner";
import { ImportFile } from "../common/ImportFile";
import { Footer } from "../common/Footer";
import { SupplierShowPageSkeleton } from "../common/skeletons";
import { SUPPLIERS_HELP_PAGE_URL } from "../../constants";
import SaveBar from "../common/SaveBar";
import {
  downloadFile,
  getVendor,
  getVendorHorseVariants,
  importVendorHorseVariants,
  updateVendor,
  useCurrencies,
  useUser,
} from "../../api_utils/requests";
import type { Vendor, VendorHorseVariantRow } from "../../api_utils/types";
import {
  assertString,
  cloneNestedObjects,
  extractMessageFromError,
  getCurrencySymbol,
  softAssertNumber,
} from "../../helper_functions/utils";

interface EditableVendorHorseVariantRow
  extends Omit<VendorHorseVariantRow, "cost" | "minimum_order_quantity" | "pack_size"> {
  sortIndex: number;
  cost: number | string | null;
  minimum_order_quantity: number | string | null;
  pack_size: number | string | null;
}
type EditableVendorHorseVariantRows = Record<number, EditableVendorHorseVariantRow>;
interface VendorHorseVariantSaveAttributes {
  horse_variant_id: number;
  minimum_order_quantity: number | string | null;
  pack_size: number | string | null;
  vendor_sku: string | null;
  cost: number | string | null;
}

const blankToNull = (value: number | string | null | undefined): number | string | null =>
  value === "" || value === undefined ? null : value;

const formatVendorToSave = ({
  email,
  currency,
  notes,
  minimum_order_value,
  delivery_lead_days,
}: Vendor): Partial<Vendor> => ({
  email,
  currency,
  notes,
  minimum_order_value,
  delivery_lead_days: delivery_lead_days === 0 ? undefined : delivery_lead_days,
});

const formatVendorHorseVariantRows = (
  vendorHorseVariantRows: VendorHorseVariantRow[],
): EditableVendorHorseVariantRows =>
  vendorHorseVariantRows.reduce<EditableVendorHorseVariantRows>((rows, vendorHorseVariantRow, sortIndex) => {
    rows[vendorHorseVariantRow.horse_variant_id] = { ...vendorHorseVariantRow, sortIndex };
    return rows;
  }, {});

const vendorHorseVariantSaveAttributes = ({
  horse_variant_id,
  minimum_order_quantity,
  pack_size,
  vendor_sku,
  cost,
}: EditableVendorHorseVariantRow): VendorHorseVariantSaveAttributes => ({
  horse_variant_id,
  minimum_order_quantity: minimum_order_quantity || 0,
  pack_size: blankToNull(pack_size),
  vendor_sku: vendor_sku || null,
  cost: blankToNull(cost),
});

const vendorHorseVariantChanged = (
  vendorHorseVariant: EditableVendorHorseVariantRow,
  originalVendorHorseVariantRows: EditableVendorHorseVariantRows,
): boolean => {
  const originalVendorHorseVariant = originalVendorHorseVariantRows[vendorHorseVariant.horse_variant_id];
  return JSON.stringify(vendorHorseVariantSaveAttributes(vendorHorseVariant)) !==
    JSON.stringify(vendorHorseVariantSaveAttributes(originalVendorHorseVariant));
};

const columnNames: NonEmptyArray<IndexTableHeading> = [
  { title: "Variant" },
  { title: "Minimum order quantity" },
  { title: "Pack size", tooltipContent: "Case quantity" },
  { title: "Vendor SKU" },
  { title: "Vendor cost" },
];

const sortOptions = [
  { label: "Created at", directionLabel: "Oldest/Newest", value: "created_at asc" },
  { label: "Created at", directionLabel: "Newest/Oldest", value: "created_at desc" },
  { label: "Title", directionLabel: "A-Z", value: "title asc" },
  { label: "Title", directionLabel: "Z-A", value: "title desc" },
  { label: "MOQ", directionLabel: "Low/High", value: "minimum_order_quantity asc" },
  { label: "MOQ", directionLabel: "High/Low", value: "minimum_order_quantity desc" },
  { label: "Pack size", directionLabel: "Low/High", value: "pack_size asc" },
  { label: "Pack size", directionLabel: "High/Low", value: "pack_size desc" },
  { label: "Vendor SKU", directionLabel: "A-Z", value: "vendor_sku asc" },
  { label: "Vendor SKU", directionLabel: "Z-A", value: "vendor_sku desc" },
  { label: "Vendor cost", directionLabel: "Low/High", value: "cost asc" },
  { label: "Vendor cost", directionLabel: "High/Low", value: "cost desc" },
] as SortButtonChoice[];

function ShowVendor(): React.JSX.Element {
  const { idOrName } = useParams();
  const navigate = useNavigate();

  const [importCSVModalIsOpen, setImportCSVModalIsOpen] = useState(false);

  const [vendor, setVendor] = useState<Vendor>({} as Vendor);
  const originalVendor = useRef<Vendor>();
  const originalVendorHorseVariants = useRef<EditableVendorHorseVariantRows>({} as EditableVendorHorseVariantRows);
  const [vendorHorseVariants, setVendorHorseVariants] = useState<EditableVendorHorseVariantRows>(
    {} as EditableVendorHorseVariantRows,
  );

  const [pageLoading, setPageLoading] = useState(true);
  const [variantsLoading, setVariantsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [hasNext, setHasNext] = useState<boolean>(false);
  const [hasPrev, setHasPrev] = useState<boolean>(false);
  const urlParams = new URLSearchParams(window.location.search);
  const [pageNum, setPageNum] = useState<number>(Number(urlParams.get("page")) || 1);
  const initSortedCol = urlParams.get("by_sort[column]") || "created_at";
  const initSortedColDir = urlParams.get("by_sort[direction]") || "desc";
  const [sort, setSort] = useState([`${initSortedCol} ${initSortedColDir}`]);
  const { mode, setMode } = useSetIndexFiltersMode();

  useEffect(() => {
    shopify.loading(loading);
  }, [loading]);

  const {
    data: {
      user: { currency: userCurrency },
    },
  } = useUser();

  const { data: currencies } = useCurrencies();

  const setVendorHorseVariantsData = (
    newVendorHorseVariants: VendorHorseVariantRow[],
    newHasNext: boolean,
    newHasPrev: boolean,
  ): void => {
    const formattedVendorHorseVariants = formatVendorHorseVariantRows(newVendorHorseVariants);
    setVendorHorseVariants(formattedVendorHorseVariants);
    originalVendorHorseVariants.current = cloneNestedObjects(formattedVendorHorseVariants);
    setHasNext(newHasNext);
    setHasPrev(newHasPrev);
  };

  useEffect(() => {
    getVendor(idOrName)
      .then(({ vendor: newVendor, canonical_path }) => {
        setVendor(newVendor);
        originalVendor.current = { ...newVendor };
        setPageLoading(false);
        setLoading(false);

        if (canonical_path !== window.location.pathname) {
          navigate(canonical_path, { replace: true });
        }
      })
      .catch((error: unknown) => {
        const message = extractMessageFromError(error);
        setErrorMessage(message);
        setPageLoading(false);
        setLoading(false);
      });
  }, [idOrName, navigate]);

  useEffect(() => {
    if (!vendor.id) return;

    const [column, direction] = sort[0].split(" ");
    getVendorHorseVariants(vendor.id, { page: pageNum, by_sort: { column, direction } })
      .then(({ rows: newVendorHorseVariants, hasNext: newHasNext, hasPrev: newHasPrev }) => {
        setVendorHorseVariantsData(newVendorHorseVariants, newHasNext, newHasPrev);
        setVariantsLoading(false);
      })
      .catch((error: unknown) => {
        const message = extractMessageFromError(error);
        setErrorMessage(message);
        setVariantsLoading(false);
      });
  }, [vendor.id, pageNum, sort]);

  const handleVendorChange = useCallback((value: string, key: string): void => {
    setVendor((previousVendor) => ({ ...previousVendor, [key]: value }));
    setIsDirty(true);
  }, []);

  const handleVendorNumberChange = useCallback((value: string, key: string): void => {
    const cleanValue = value?.replace(/[^\d.-]/g, "");
    setVendor((previousVendor) => ({ ...previousVendor, [key]: cleanValue }));
    setIsDirty(true);
  }, []);

  const handleHorseVariantsChange = useCallback(
    (horseVariantId: number, value: number | string, key: string) => {
      const newVendorHorseVariants = { ...vendorHorseVariants };
      newVendorHorseVariants[horseVariantId] = {
        ...newVendorHorseVariants[horseVariantId],
        [key]: value,
      } as EditableVendorHorseVariantRow;
      setVendorHorseVariants(newVendorHorseVariants);
      setIsDirty(true);
    },
    [vendorHorseVariants],
  );

  const changedVendorHorseVariantAttributes = useCallback((): VendorHorseVariantSaveAttributes[] => {
    return Object.values(vendorHorseVariants)
      .filter((vendorHorseVariant) =>
        vendorHorseVariantChanged(vendorHorseVariant, originalVendorHorseVariants.current),
      )
      .map(vendorHorseVariantSaveAttributes);
  }, [vendorHorseVariants]);

  const handleSaveVendor = useCallback(() => {
    setLoading(true);
    const payload = {
      vendor: {
        ...formatVendorToSave(vendor),
        vendor_horse_variants_attributes: changedVendorHorseVariantAttributes(),
      },
    };

    return updateVendor(vendor.id, payload)
      .then((res) => {
        shopify.toast.show("Save successful");
        setIsDirty(false);
        setVendor(res.vendor);
        originalVendor.current = { ...res.vendor };
        originalVendorHorseVariants.current = cloneNestedObjects(vendorHorseVariants);
      })
      .catch((error: unknown) => {
        Rollbar.error(error, payload);
        const message = extractMessageFromError(error);
        setErrorMessage(message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [changedVendorHorseVariantAttributes, vendor, vendorHorseVariants]);

  const handleDiscardAction = useCallback(() => {
    setVendor({ ...originalVendor.current });
    setVendorHorseVariants(cloneNestedObjects(originalVendorHorseVariants.current));
    setIsDirty(false);
  }, [originalVendor, originalVendorHorseVariants]);

  const setPageNumAndScroll = useCallback(
    async (newPageNum: number) => {
      setLoading(true);
      setPageNum(newPageNum);
      const [column, direction] = sort[0].split(" ");
      await getVendorHorseVariants(vendor.id, { page: newPageNum, by_sort: { column, direction } }).then(
        ({ rows: newVendorHorseVariants, hasNext: newHasNext, hasPrev: newHasPrev }) => {
          setVendorHorseVariantsData(newVendorHorseVariants, newHasNext, newHasPrev);
          setLoading(false);

          document.getElementsByClassName("Polaris-IndexTable__Table")[0]?.scrollIntoView();
        },
      );
    },
    [vendor.id, sort],
  );
  const nextPage = useCallback(async () => {
    if (hasNext && !isDirty) {
      await setPageNumAndScroll(pageNum + 1);
    }
  }, [hasNext, isDirty, pageNum, setPageNumAndScroll]);
  const prevPage = useCallback(async () => {
    if (hasPrev && !isDirty) {
      await setPageNumAndScroll(pageNum - 1);
    }
  }, [hasPrev, isDirty, pageNum, setPageNumAndScroll]);

  const sortedVendorHorseVariants = useMemo(() => {
    return Object.values(vendorHorseVariants).sort((left, right) => left.sortIndex - right.sortIndex);
  }, [vendorHorseVariants]);

  const emptyState = <EmptyState heading="No variants found" image="" />;

  const pageMarkup = vendor && (
    <Page
      actionGroups={[
        {
          title: "CSV",
          actions: [
            {
              content: "Export",
              onAction: async (): Promise<void> => {
                const filename = `Horse Vendor ${vendor.name}.csv`;
                await downloadFile(`/vendors/${vendor.id}.csv${window.location.search}`, filename);
              },
              icon: ExportIcon,
            },
            {
              content: "Import",
              disabled: isDirty,
              onAction: (): void => {
                setImportCSVModalIsOpen(true);
              },
              icon: ImportIcon,
            },
          ],
        },
      ]}
      backAction={{
        content: "Vendors",
        onAction: async () => {
          await shopify.saveBar.leaveConfirmation();

          navigate("/vendors");
        },
      }}
      title={vendor.name}
    >
      <SaveBar handleDiscard={handleDiscardAction} handleSave={handleSaveVendor} isDirty={isDirty} />

      <ImportFile<VendorHorseVariantRow>
        apiCallMethod={async (formData) => await importVendorHorseVariants(vendor.id, formData)}
        modalIsOpen={importCSVModalIsOpen}
        setApiResponse={async () => {
          const [column, direction] = sort[0].split(" ");
          await getVendorHorseVariants(vendor.id, { page: pageNum, by_sort: { column, direction } })
            .then(({ rows, hasNext: newHasNext, hasPrev: newHasPrev }) => {
              setVendorHorseVariantsData(rows, newHasNext, newHasPrev);
            })
            .catch((error: unknown) => {
              setErrorMessage(extractMessageFromError(error));
            });
        }}
        setErrorMessage={setErrorMessage}
        setModalIsOpen={setImportCSVModalIsOpen}
        title="Import vendor CSV"
      />

      <Layout>
        {errorMessage ? (
          <Layout.Section variant="fullWidth">
            <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
          </Layout.Section>
        ) : null}
        <Layout.Section>
          <LegacyCard>
            <LegacyCard.Section>
              <FormLayout>
                <TextField autoComplete="on" disabled id="name" label="Name" value={vendor.name} />
                <TextField
                  autoComplete="on"
                  id="email"
                  label="Contact email"
                  onChange={handleVendorChange}
                  placeholder="contact@example.com"
                  value={assertString(vendor.email)}
                />
                <TextField
                  autoComplete="on"
                  id="notes"
                  label="Purchase order notes"
                  multiline={2}
                  onChange={handleVendorChange}
                  value={assertString(vendor.notes)}
                />
                <FormLayout.Group condensed>
                  <TextField
                    autoComplete="off"
                    helpText="Used as the default arrival date when creating new purchase orders"
                    id="delivery_lead_days"
                    label="Delivery lead days"
                    min={0}
                    onChange={handleVendorNumberChange}
                    suffix="days"
                    type="integer"
                    value={assertString(vendor.delivery_lead_days)}
                  />
                  <TextField
                    autoComplete="on"
                    id="minimum_order_value"
                    label="Minimum Order Value"
                    onChange={handleVendorNumberChange}
                    suffix={vendor.currency || userCurrency}
                    type="number"
                    value={assertString(vendor.minimum_order_value)}
                  />
                  <Select
                    id="currency"
                    label="Currency"
                    onChange={handleVendorChange}
                    options={currencies}
                    value={vendor.currency || userCurrency}
                  />
                </FormLayout.Group>
              </FormLayout>
            </LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>
        <Layout.Section>
          <LegacyCard>
            <IndexFilters
              disableQueryField
              disabled={isDirty}
              filters={[]}
              hideFilters
              hideQueryField
              mode={mode}
              onClearAll={() => {}}
              onQueryChange={() => {}}
              onQueryClear={() => {}}
              onSort={setSort}
              selected={0}
              setMode={setMode}
              sortOptions={sortOptions}
              sortSelected={sort}
              tabs={[]}
            />
            <IndexTable
              emptyState={emptyState}
              headings={columnNames}
              itemCount={sortedVendorHorseVariants.length}
              loading={variantsLoading}
              pagination={{
                hasPrevious: hasPrev && !isDirty,
                previousKeys: [37],
                onPrevious: prevPage,
                hasNext: hasNext && !isDirty,
                nextKeys: [39],
                onNext: nextPage,
              }}
              selectable={false}
            >
              {sortedVendorHorseVariants.map((variant: EditableVendorHorseVariantRow, index) => (
                <IndexTable.Row
                  id={variant.horse_variant_id.toString()}
                  key={variant.horse_variant_id}
                  position={index}
                >
                  <IndexTable.Cell>
                    <div className="medium_hv">
                      <HorseVariant key={variant.horse_variant_id} variant={variant} />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div className="w120">
                      <TextField
                        autoComplete="off"
                        disabled={variant.discarded}
                        id={`minimum_order_quantity_${variant.horse_variant_id}`}
                        label="Minimum order quantity"
                        labelHidden
                        min={1}
                        name="Minimum order quantity"
                        onChange={(value: string) => {
                          handleHorseVariantsChange(
                            variant.horse_variant_id,
                            softAssertNumber(value),
                            "minimum_order_quantity",
                          );
                        }}
                        type="integer"
                        value={assertString(variant.minimum_order_quantity)}
                      />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div className="w120">
                      <TextField
                        autoComplete="off"
                        disabled={variant.discarded}
                        id={`pack_size_${variant.horse_variant_id}`}
                        label="Pack size"
                        labelHidden
                        min={0}
                        name="Pack size"
                        onChange={(value: string) => {
                          handleHorseVariantsChange(variant.horse_variant_id, softAssertNumber(value), "pack_size");
                        }}
                        type="integer"
                        value={assertString(variant.pack_size)}
                      />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div className="w120">
                      <TextField
                        autoComplete="off"
                        disabled={variant.discarded}
                        id={`vendor_sku_${variant.horse_variant_id}`}
                        label="Vendor SKU"
                        labelHidden
                        name="Vendor SKU"
                        onChange={(value: string) => {
                          handleHorseVariantsChange(variant.horse_variant_id, value, "vendor_sku");
                        }}
                        value={assertString(variant.vendor_sku)}
                      />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div className="w120">
                      <TextField
                        autoComplete="off"
                        disabled={variant.discarded}
                        id={`cost_${variant.horse_variant_id}`}
                        label="Vendor cost"
                        labelHidden
                        min={0.0}
                        name="Vendor cost"
                        onChange={(value: string) => {
                          handleHorseVariantsChange(variant.horse_variant_id, value, "cost");
                        }}
                        prefix={getCurrencySymbol(vendor.currency || userCurrency)}
                        step={0.01}
                        type="currency"
                        value={assertString(variant.cost)}
                      />
                    </div>
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          </LegacyCard>
        </Layout.Section>
      </Layout>
      <Footer pageTitle="vendors" url={SUPPLIERS_HELP_PAGE_URL} />
    </Page>
  );

  return pageLoading ? <SupplierShowPageSkeleton /> : pageMarkup;
}

export default ShowVendor;
